import React, { useState, useEffect } from 'react';
import { 
  Typography, Paper, Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, Button, Box, IconButton, Chip
} from '@mui/material';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddIcon from '@mui/icons-material/Add';

const ReturnsList = () => {
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchReturns = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/returns');
      setReturns(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching returns:', err);
      setError('Virhe palautusten haussa. Yritä myöhemmin uudelleen.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReturns();
  }, []);

  const handleDelete = async (id) => {
    if (window.confirm('Haluatko varmasti poistaa tämän palautuksen?')) {
      try {
        await api.delete(`/api/returns/${id}`);
        // Refresh the list after deletion
        fetchReturns();
      } catch (err) {
        console.error('Error deleting return:', err);
        setError('Virhe palautuksen poistossa. Yritä myöhemmin uudelleen.');
      }
    }
  };

  const getStatusChip = (status) => {
    let color = 'default';
    let label = status || 'Tuntematon';

    switch(status) {
      case 'pending':
        color = 'warning';
        label = 'Käsittelyssä';
        break;
      case 'completed':
        color = 'success';
        label = 'Valmis';
        break;
      case 'rejected':
        color = 'error';
        label = 'Hylätty';
        break;
      default:
        break;
    }

    return <Chip label={label} color={color} size="small" />;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('fi-FI');
  };

  return (
    <div>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Palautukset
        </Typography>
        <Button 
          variant="contained" 
          color="primary" 
          component={Link} 
          to="/returns/new"
          startIcon={<AddIcon />}
        >
          Uusi palautus
        </Button>
      </Box>

      {error && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: '#ffebee' }}>
          <Typography color="error">{error}</Typography>
        </Paper>
      )}

      {loading ? (
        <Typography>Ladataan palautuksia...</Typography>
      ) : returns.length === 0 ? (
        <Paper sx={{ p: 3 }}>
          <Typography>Ei palautuksia. Luo uusi palautus aloittaaksesi.</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Asiakas</TableCell>
                <TableCell>Lasku</TableCell>
                <TableCell>Tilaus</TableCell>
                <TableCell>Ilmoitus pvm</TableCell>
                <TableCell>Myyjä</TableCell>
                <TableCell>Mitä palautetaan</TableCell>
                <TableCell>Palautus pvm</TableCell>
                <TableCell>Tila</TableCell>
                <TableCell>Toiminnot</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {returns.map((returnItem) => (
                <TableRow key={returnItem.id}>
                  <TableCell>{returnItem.customer_name || '-'}</TableCell>
                  <TableCell>{returnItem.invoice_number || '-'}</TableCell>
                  <TableCell>{returnItem.order_number || '-'}</TableCell>
                  <TableCell>{formatDate(returnItem.notification_date)}</TableCell>
                  <TableCell>{returnItem.seller_name || '-'}</TableCell>
                  <TableCell>{returnItem.items_to_return || '-'}</TableCell>
                  <TableCell>{formatDate(returnItem.return_date)}</TableCell>
                  <TableCell>{getStatusChip(returnItem.status)}</TableCell>
                  <TableCell>
                    <IconButton 
                      component={Link} 
                      to={`/returns/${returnItem.id}`}
                      color="info"
                      size="small"
                      title="Näytä tiedot"
                    >
                      <VisibilityIcon />
                    </IconButton>
                    <IconButton 
                      component={Link} 
                      to={`/returns/edit/${returnItem.id}`}
                      color="primary"
                      size="small"
                      title="Muokkaa"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton 
                      onClick={() => handleDelete(returnItem.id)}
                      color="error"
                      size="small"
                      title="Poista"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </div>
  );
};

export default ReturnsList;
